// Онбординг, экран 0 — выбор языка интерфейса.
// Применяется немедленно (онбординг уже на нём) и персистится в users.language
// через onboarding.setLanguage. Язык AI-генерации не спрашиваем
// (answerLocale=null → следует языку интерфейса; сменить можно в Настройках).
//
// Экран построен до общего каркаса `OnboardingStepLayout` и держит свою
// раскладку — переписывать его новая последовательность не требовала. Общий у
// него с остальными только адрес следующего шага: он берётся из `_steps.ts`,
// а не пишется здесь строкой.

import { Combobox, cn, ElevatedPanel, PANEL_PAD } from "@studyhub/ui";
import { IconLanguage } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { OnboardingFooter } from "@/components/onboarding/onboarding-footer";
import { OnboardingShell } from "@/components/onboarding/onboarding-shell";
import { useOnboardingStep } from "@/components/onboarding/use-onboarding-step";
import { useSupportedLocales } from "@/hooks/use-supported-locales";
import { toLocaleComboboxOption } from "@/i18n/locale-flags";
import type { SupportedLocale } from "@/i18n/locales";
import { useLocale } from "@/i18n/use-locale";
import { useIsLgUp } from "@/lib/platform-detect";
import { resolveDefaultTimezone } from "@/lib/timezones";
import { api } from "@/lib/trpc-client";
import { useAuth } from "@/providers/auth-provider";

export const Route = createFileRoute("/onboarding/language")({
	component: LanguagePage,
});

function LanguagePage() {
	const { t } = useTranslation("onboarding");
	const step = useOnboardingStep("language");
	const isDesktop = useIsLgUp();
	const { locale, setLocale } = useLocale();
	const { isAuthenticated } = useAuth();
	const setLanguage = api.onboarding.setLanguage.useMutation();

	const [uiLocale, setUiLocale] = useState<SupportedLocale>(locale);
	const { locales } = useSupportedLocales();

	const uiOptions = useMemo(() => locales.map(toLocaleComboboxOption), [locales]);

	// Смена UI-языка применяется мгновенно — экран и тур локализуются — и сразу
	// же уезжает в профиль, не дожидаясь кнопки «Далее»: иначе выбор, сделанный
	// на телефоне, не доезжал бы до ноутбука, а обновление страницы возвращало
	// бы язык из локального хранилища вместо выбранного.
	//
	// Это не черновик, а настоящая запись, и она безопасна: `setLanguage` —
	// идемпотентный UPDATE профиля, он ничего не «засчитывает» и не закрывает
	// пунктов чек-листа (в отличие от настроек ритма, которым автозапись как раз
	// противопоказана — см. `rhythm.tsx`).
	const handleUiChange = (v: string): void => {
		const next = v as SupportedLocale;
		setUiLocale(next);
		void setLocale(next);
		if (!isAuthenticated) return;
		setLanguage.mutate(
			{ language: next, answerLocale: null, timezone: resolveDefaultTimezone(next) },
			{
				onError: () => {
					// Офлайн или 401. Язык уже применён локально, а «Далее» повторит
					// запись — ронять экран на фоновой записи нельзя.
				},
			},
		);
	};

	const handleNext = async (): Promise<void> => {
		try {
			if (isAuthenticated) {
				// Ленивый сбор timezone: browser TZ, но Europe/Moscow и др. RU-зоны
				// только при uiLocale=ru — иначе ближайшая не-российская зона с тем же offset.
				const timezone = resolveDefaultTimezone(uiLocale);
				// answerLocale=null → язык генерации следует языку интерфейса (дефолт).
				await setLanguage.mutateAsync({ language: uiLocale, answerLocale: null, timezone });
			}
			step.goNext();
		} catch {
			// 401 → AuthExpiredModal; иначе остаёмся на шаге.
		}
	};

	const cardContent = (
		<ElevatedPanel className={cn(PANEL_PAD, "w-full")}>
			<div className="space-y-6">
				<header className="flex items-start gap-3">
					<div className="flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-tile)] bg-primary/10 [box-shadow:inset_0_1px_0_rgb(255_255_255/0.4)]">
						<IconLanguage size={20} className="text-primary" stroke={1.75} />
					</div>
					<div className="space-y-1">
						<h1 className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
							{t("language.title")}
						</h1>
						<p className="text-md text-muted-foreground">{t("language.subtitle")}</p>
					</div>
				</header>

				<div className="space-y-2">
					<label className="text-sm font-medium text-foreground" htmlFor="ui-language">
						{t("language.uiLanguageLabel")}
					</label>
					<Combobox
						options={uiOptions}
						value={uiLocale}
						onChange={handleUiChange}
						placeholder={t("language.uiLanguagePlaceholder")}
						searchPlaceholder={t("language.uiLanguagePlaceholder")}
						className="h-11 lg:h-9"
					/>
				</div>

				{isDesktop && (
					<OnboardingFooter
						onNext={() => void handleNext()}
						nextDisabled={false}
						isNextLoading={setLanguage.isPending}
						hideBack
						variant="inline"
						nextLabel={t("language.continue")}
					/>
				)}
			</div>
		</ElevatedPanel>
	);

	return (
		<OnboardingShell>
			<div className="mx-auto w-full max-w-xl flex-1 px-4 pt-2 pb-6 sm:px-6 md:pt-3 md:pb-8">
				{cardContent}
			</div>
			{!isDesktop && (
				<OnboardingFooter
					onNext={() => void handleNext()}
					nextDisabled={false}
					isNextLoading={setLanguage.isPending}
					hideBack
					variant="sticky"
					nextLabel={t("language.continue")}
				/>
			)}
		</OnboardingShell>
	);
}
